/**
 * Odmiana jednostek liczonych („1 dzień" / „3 dni" / „7 dni").
 *
 * Zasoby i18n mają trzy formy per jednostka w OBU językach — dzięki temu
 * zestawy kluczy pl/en są identyczne (wymóg testu parytetu tłumaczeń),
 * a wybór formy robi ten helper: dla polskiego regułami CLDR, dla angielskiego
 * prostym „1 albo reszta".
 */
import { pluralPL } from '@k1p4/i18n'
import type { TFunction } from 'i18next'

export type CountedUnit = 'day' | 'document' | 'person' | 'specificTask'

/*
 * `fraction` — forma dla liczby niecałkowitej: „10,5 dnia", nie „10,5 dni"
 * (Fala 7, grupa 24: dni urlopu wyprowadzone z godzin bywają ułamkiem).
 */
const KEYS: Record<CountedUnit, { one: string; few: string; many: string; fraction?: string }> = {
  day: {
    one: 'portal.units.dayOne',
    few: 'portal.units.dayFew',
    many: 'portal.units.dayMany',
    fraction: 'portal.units.dayFraction',
  },
  document: {
    one: 'portal.units.documentOne',
    few: 'portal.units.documentFew',
    many: 'portal.units.documentMany',
  },
  person: {
    one: 'hrPayroll.units.personOne',
    few: 'hrPayroll.units.personFew',
    many: 'hrPayroll.units.personMany',
  },
  specificTask: {
    one: 'hrPayroll.units.specificTaskOne',
    few: 'hrPayroll.units.specificTaskFew',
    many: 'hrPayroll.units.specificTaskMany',
  },
}

export function pluralUnit(
  language: string,
  count: number,
  t: TFunction,
  unit: CountedUnit,
): string {
  const keys = KEYS[unit]
  if (language.startsWith('pl')) {
    if (!Number.isInteger(count) && keys.fraction) return t(keys.fraction)
    return pluralPL(count, { one: t(keys.one), few: t(keys.few), many: t(keys.many) })
  }
  return count === 1 ? t(keys.one) : t(keys.many)
}

/**
 * Liczba do kafla i noty w języku ekranu: po polsku przecinek dziesiętny
 * (10,5), po angielsku kropka — bez zbędnych zer i najwyżej dwa miejsca.
 * Dni urlopu z godzin puli bywają ułamkiem, a `String(10.5)` dawał „10.5"
 * także po polsku.
 */
export function formatCount(language: string, value: number): string {
  return new Intl.NumberFormat(language.startsWith('pl') ? 'pl-PL' : 'en-GB', {
    maximumFractionDigits: 2,
  }).format(value)
}
